import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  signal,
  SimpleChanges,
  ViewChild,
  OnInit,
  OnChanges,
} from '@angular/core';
import {
  normalizeUnitValue,
  serializeUnitValue,
  type UnitValue,
} from '@front-platform/content-builder';
import { DOOD_URL } from '@config/ws.config';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ISimpleItem, ISubItem } from '@core/models/simple-item.model';
import { staggeredListAnimation } from '@shared/animations/list.animation';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';

@Component({
  selector: 'app-kiosk-vertical-category-list-atom',
  templateUrl: './kiosk-vertical-category-list-atom.component.html',
  styleUrls: ['./kiosk-vertical-category-list-atom.component.scss'],
  animations: [staggeredListAnimation],
  standalone: false,
})
export class KioskVerticalCategoryListAtomComponent
  implements AfterViewInit, OnDestroy, OnInit, OnChanges
{
  @Input() items!: ISimpleItem[];
  @Input() activeItem?: ISimpleItem;
  @Input() activeSubItem?: ISubItem;
  @Input() displayItems!: ISimpleItem[];
  @Input() backgroundColor?: IContentBuilderFieldColor;
  @Input() itemBackgroundColor?: IContentBuilderFieldColor;
  @Input() itemSelectedBackgroundColor?: IContentBuilderFieldColor;
  @Input() itemTextColor?: IContentBuilderFieldColor;
  @Input() itemSelectedTextColor?: IContentBuilderFieldColor;
  @Input() accentColor?: IContentBuilderFieldColor;
  @Input() displayCategoryName = true;
  @Input() categoryImageRatio = '2/1';
  @Input() categoryCardPadding = true;
  @Input() categoryCardGap: UnitValue | number = { unit: 'px', value: 10 };
  /** Absent config stays square. New blocs pass the 0.5rem creation default. */
  @Input() categoryCardBorderRadius: UnitValue | number = { unit: 'px', value: 0 };
  @Input() categoryCardsPosition: 'top' | 'center' | 'bottom' | string = 'top';
  @Input() displayArrows = true;
  @Input() displayDots = true;
  @Input() displaySelectedCategoryBorder = false;
  @Input() selectedCategoryBorderColor?: IContentBuilderFieldColor;
  @Input() unselectedCategoryBorderColor?: IContentBuilderFieldColor;
  @Input() selectedCategoryBorderWidth: UnitValue | number = { unit: 'px', value: 1 };
  @Input() displaySelectedCategoryHalo = false;
  @Input() selectedCategoryHaloColor?: IContentBuilderFieldColor;
  @Input() selectedCategoryHaloOpacity = 40;
  @Input() selectedCategoryShadowSpread: UnitValue | number = { unit: 'px', value: 8 };
  @Input() displaySelectedCategoryZoom = false;
  @Input() selectedCategoryZoomScale: UnitValue | number = { unit: '%', value: 108 };
  /** Preview-only: category value shown as the selected card. */
  @Input() selectedItemId?: string | null;

  @Output() categoryChange = new EventEmitter<string>();
  @Output() searchEvent = new EventEmitter<Event>();
  @Output() isBurgerNavigationOpen = new EventEmitter<boolean>();

  @ViewChild('scrollContainer') scrollContainer: ElementRef | undefined;
  @ViewChild('scrollUpIcon') scrollUpIcon: ElementRef | undefined;
  @ViewChild('scrollDownIcon') scrollDownIcon: ElementRef | undefined;

  scrollAmount = 100;
  scrollInterval: any;
  activeIndex = 0;
  selectedIndex: number | null = null;

  isScrollIndicatorVisible = signal(false);
  private scrollHideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private renderer: Renderer2) {}

  ngOnInit(): void {
    this.displayItems = this.items;
    if (this.selectedItemId) {
      this.syncSelectedIndexFromSelectedItemId();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeItem) {
      this.detectActiveItem();
    }

    if (changes['items']) {
      this.displayItems = this.items;
    }

    if (changes['selectedItemId'] || (changes['items'] && this.selectedItemId)) {
      this.syncSelectedIndexFromSelectedItemId();
    }
  }

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      this.renderer.listen(this.scrollContainer.nativeElement, 'scroll', (_event: Event) => {
        this.onScroll();
      });
      this.updateScrollIconsOpacity();
    }
  }

  startScroll(direction: 'up' | 'down'): void {
    this.scrollInterval = setInterval(() => {
      this.scrollTo(direction);
    }, 10);
  }

  stopScroll(): void {
    clearInterval(this.scrollInterval);
  }

  scrollTo(direction: 'up' | 'down'): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.scrollBy({
        top: direction === 'up' ? -this.scrollAmount : this.scrollAmount,
        behavior: 'smooth',
      });
      this.detectActiveItem();
      this.updateScrollIconsOpacity();
    }
  }

  categoryClick(item: ISimpleItem, index: number): void {
    this.activeItem = item;
    this.selectedIndex = index;
    this.categoryChange.emit(item.value);
    this.centerActiveItem(index);
  }

  setActiveItem(index: number): void {
    if (this.activeIndex !== index) {
      this.activeIndex = index;
    }
  }

  onScroll(): void {
    this.detectActiveItem();
    this.updateScrollIconsOpacity();
    this.showScrollIndicatorTemporarily();
  }

  private showScrollIndicatorTemporarily(): void {
    if (this.scrollHideTimeout) {
      clearTimeout(this.scrollHideTimeout);
      this.scrollHideTimeout = null;
    }

    if (!this.isScrollIndicatorVisible()) {
      this.isScrollIndicatorVisible.set(true);
    }

    this.scrollHideTimeout = setTimeout(() => {
      this.isScrollIndicatorVisible.set(false);
      this.scrollHideTimeout = null;
    }, 1500);
  }

  ngOnDestroy(): void {
    if (this.scrollHideTimeout) clearTimeout(this.scrollHideTimeout);
  }

  detectActiveItem(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      const scrollTop = container.scrollTop;
      const containerHeight = container.scrollHeight - container.clientHeight;

      const scrollPercentage = containerHeight ? scrollTop / containerHeight : 0;

      const totalItems = this.displayItems.length;
      const calculatedIndex = Math.round(scrollPercentage * (totalItems - 1));

      this.setActiveItem(calculatedIndex);
    }
  }

  centerActiveItem(index: number): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      const itemElement = container.children[index] as HTMLElement;
      if (itemElement) {
        const itemOffsetTop = itemElement.offsetTop;
        const containerHeight = container.clientHeight;
        const itemHeight = itemElement.clientHeight;

        container.scrollTo({
          top: itemOffsetTop - containerHeight / 2 + itemHeight / 2,
          behavior: 'smooth',
        });
      }
    }
  }

  updateScrollIconsOpacity(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container && this.scrollUpIcon && this.scrollDownIcon) {
      const scrollTop = container.scrollTop;
      const maxScrollTop = container.scrollHeight - container.clientHeight;

      const upOpacity = scrollTop === 0 ? 0.5 : 1;
      const downOpacity = scrollTop >= maxScrollTop ? 0.5 : 1;

      this.renderer.setStyle(this.scrollUpIcon.nativeElement, 'opacity', upOpacity);
      this.renderer.setStyle(this.scrollDownIcon.nativeElement, 'opacity', downOpacity);
    }
  }

  toggleSearch(): void {
    this.searchEvent.emit();
  }

  get categoryImageAspectRatio(): string {
    const raw = String(this.categoryImageRatio ?? '2/1').trim();
    return (raw || '2/1').replace(/:/g, '/');
  }

  get categoryCardGapCss(): string {
    return serializeUnitValue(normalizeUnitValue(this.categoryCardGap, { unit: 'px', value: 10 }));
  }

  get categoryCardBorderRadiusCss(): string {
    return serializeUnitValue(
      normalizeUnitValue(this.categoryCardBorderRadius, { unit: 'px', value: 0 }),
    );
  }

  get selectedCategoryBorderWidthCss(): string {
    return serializeUnitValue(
      normalizeUnitValue(this.selectedCategoryBorderWidth, { unit: 'px', value: 1 }),
    );
  }

  /**
   * Room so the selected card's shadow / scale is not clipped by the scrollport
   * (`overflow-y` also clips X). Inline padding is paired with a negative margin
   * so card width stays the column width. Block padding keeps the first and last
   * cards fully visible, including their halo.
   */
  get selectionOverflowInline(): string {
    const blur = this.displaySelectedCategoryHalo
      ? normalizeUnitValue(this.selectedCategoryShadowSpread, { unit: 'px', value: 8 })
      : null;
    const blurCss = blur && blur.value > 0 ? serializeUnitValue(blur) : null;

    let zoomCss: string | null = null;
    if (this.displaySelectedCategoryZoom) {
      const zoom = normalizeUnitValue(this.selectedCategoryZoomScale, {
        unit: '%',
        value: 108,
      });
      const factor = zoom.unit === '%' ? zoom.value / 100 : zoom.value;
      if (Number.isFinite(factor) && factor > 1) {
        zoomCss = `${((factor - 1) / (2 * factor)) * 100}%`;
      }
    }

    if (blurCss && zoomCss) {
      return `calc(${blurCss} + ${zoomCss})`;
    }
    return blurCss ?? zoomCss ?? '0px';
  }

  get selectedCategoryHaloOpacityPercent(): number {
    const value = Number(this.selectedCategoryHaloOpacity);
    if (!Number.isFinite(value)) {
      return 40;
    }
    return Math.min(100, Math.max(0, value));
  }

  isCategorySelected(index: number): boolean {
    return this.selectedIndex === index;
  }

  private syncSelectedIndexFromSelectedItemId(): void {
    if (!this.selectedItemId || !this.displayItems?.length) {
      this.selectedIndex = null;
      return;
    }
    const index = this.displayItems.findIndex(item => item.value === this.selectedItemId);
    this.selectedIndex = index >= 0 ? index : null;
  }

  selectedCategoryBorderColorCss(isSelected: boolean): string | null {
    if (!this.displaySelectedCategoryBorder) {
      return null;
    }
    if (!isSelected) {
      return fromContentBuilderColor(this.unselectedCategoryBorderColor, 'transparent');
    }
    return fromContentBuilderColor(this.selectedCategoryBorderColor, 'primary-600');
  }

  selectedCategoryBoxShadow(isSelected: boolean): string | null {
    if (!this.displaySelectedCategoryHalo || !isSelected) {
      return null;
    }
    const blur = normalizeUnitValue(this.selectedCategoryShadowSpread, {
      unit: 'px',
      value: 8,
    });
    if (blur.value <= 0) {
      return null;
    }
    const color = fromContentBuilderColor(this.selectedCategoryHaloColor, 'primary-600');
    const opacity = this.selectedCategoryHaloOpacityPercent;
    const shadowColor =
      opacity >= 100 ? color : `color-mix(in srgb, ${color} ${opacity}%, transparent)`;
    // Standard CSS shadow: blur fades the color as it leaves the card (not a solid ring).
    return `0 0 ${serializeUnitValue(blur)} ${shadowColor}`;
  }

  selectedCategoryZoomTransform(isSelected: boolean): string | null {
    if (!this.displaySelectedCategoryZoom || !isSelected) {
      return null;
    }
    const zoom = normalizeUnitValue(this.selectedCategoryZoomScale, {
      unit: '%',
      value: 108,
    });
    const factor = zoom.unit === '%' ? zoom.value / 100 : zoom.value;
    if (!Number.isFinite(factor) || factor <= 1) {
      return null;
    }
    return `scale(${factor})`;
  }

  /**
   * `safe` falls back to the start when the list overflows, so the first and
   * last cards stay reachable. Plain `center` / `flex-end` clip both ends.
   */
  get categoryCardsJustifyContent(): string {
    switch (this.categoryCardsPosition) {
      case 'center':
        return 'safe center';
      case 'bottom':
        return 'safe flex-end';
      default:
        return 'flex-start';
    }
  }

  categoryImageUrl(icon: string) {
    // Preview fixtures may pass absolute asset/http URLs instead of storage ids.
    if (
      icon.startsWith('/') ||
      icon.startsWith('http://') ||
      icon.startsWith('https://') ||
      icon.startsWith('data:')
    ) {
      return icon;
    }
    return `${DOOD_URL}/api/storages/${icon}/preview/${icon}.png`;
  }
}
